/**
 * Drawer Search Functionality
 * Handles predictive search within the drawer
 * 
 * @requires ThemeUtils (utils.js)
 */

class DrawerSearch {
  constructor() {
    this.searchInput = null;
    this.searchResults = null;
    this.searchSuggestions = null;
    this.searchLoading = null;
    this.searchRemoveBtn = null;
    this.searchTimeout = null;
    this.currentController = null;
    
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Listen for search drawer open
    document.addEventListener('openDrawer', (event) => {
      if (event.detail.drawerId === 'search') {
        this.initializeSearchElements();
        this.focusSearchInput();
      }
    });

    // Delegated event listeners for better performance
    document.addEventListener('input', (event) => {
      if (event.target.matches('#search-input-drawer')) {
        this.handleSearchInput(event);
      }
    });

    document.addEventListener('submit', (event) => {
      if (event.target.matches('.search-form') && event.target.closest('[data-drawer-id="search"]')) {
        this.handleSearchSubmit(event);
      }
    });

    document.addEventListener('click', (event) => {
      // Handle remove button
      if (event.target.matches('#search-remove-btn')) {
        this.clearSearch();
        return;
      }

      // Handle Add to Cart button in search results
      const atcBtn = event.target.closest('.search-atc-btn');
      if (atcBtn) {
        event.preventDefault();
        window.AddToCart?.handleSubmit(
          { preventDefault: () => {}, target: { tagName: 'BUTTON' } },
          atcBtn
        );
        return;
      }

      // Handle drawer close
      if (event.target.closest('.js-drawer-close') && 
          event.target.closest('[data-drawer-id="search"]')) {
        this.clearSearch();
      }
    });

    // Size pill selection inside search results
    document.addEventListener('click', (event) => {
      const sizeBtn = event.target.closest('.search-product-item .card-product__size-btn');
      if (!sizeBtn || sizeBtn.disabled) return;
      event.preventDefault();
      event.stopPropagation();

      const wrapper = sizeBtn.closest('.search-product-item');
      if (!wrapper) return;

      wrapper.querySelectorAll('.card-product__size-btn').forEach(b => b.classList.remove('is-selected'));
      sizeBtn.classList.add('is-selected');

      const variantId = sizeBtn.dataset.variantId;
      const atcBtn = wrapper.querySelector('.search-atc-btn');
      if (atcBtn) {
        atcBtn.dataset.variantId = variantId;
        atcBtn.disabled = false;
        const span = atcBtn.querySelector('.card-product__quick-add-text');
        if (span) span.textContent = window.theme?.strings?.addToCart || '';
      }
    });
  }

  initializeSearchElements() {
    this.searchInput = document.getElementById('search-input-drawer');
    this.searchResults = document.getElementById('search-results');
    this.searchSuggestions = document.getElementById('search-suggestions');
    this.searchLoading = document.getElementById('search-loading');
    this.searchRemoveBtn = document.getElementById('search-remove-btn');
  }

  ensureElements() {
    if (!this.searchSuggestions) {
      this.initializeSearchElements();
    }
  }

  focusSearchInput() {
    ThemeUtils.wait(300).then(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    });
  }

  handleSearchInput(event) {
    this.ensureElements();
    const query = event.target.value.trim();
    
    this.toggleRemoveButton(query.length > 0);
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Cancel any in-flight request
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }

    if (query.length < 2) {
      this.showSuggestions();
      return;
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    const query = this.searchInput?.value.trim();
    
    if (query && query.length > 0) {
      const locale = window.Shopify?.locale;
      const localePrefix = locale && locale !== 'en' ? `/${locale}` : '';
      window.location.href = `${localePrefix}/search?q=${encodeURIComponent(query)}`;
    }
  }

  async performSearch(query) {
    // Cancel previous request
    if (this.currentController) {
      this.currentController.abort();
    }
    
    this.currentController = new AbortController();
    this.showLoading();

    try {
      const data = await this.fetchSearchResults(query);
      const products = this.extractProducts(data);
      const enriched = await this.enrichWithVariants(products);
      this.displaySearchResults(enriched, query);
    } catch (error) {
      if (error.message !== 'Request was cancelled') {
        this.showError();
      }
    } finally {
      this.currentController = null;
    }
  }

  async fetchSearchResults(query) {
    const encodedQuery = encodeURIComponent(query);
    
    // Try predictive search API first
    const locale = window.Shopify?.locale;
    const localePrefix = locale && locale !== 'en' ? `/${locale}` : '';
    try {
      const response = await fetch(
        `${localePrefix}/search/suggest.json?q=${encodedQuery}&resources[type]=product&resources[limit]=8`,
        { signal: this.currentController.signal }
      );
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('Request was cancelled');
    }
    
    // Fallback to products.json
    try {
      const response = await fetch(
        `/products.json?limit=8`,
        { signal: this.currentController.signal }
      );
      
      if (response.ok) {
        const data = await response.json();
        const queryLower = query.toLowerCase();
        
        // Filter products matching query
        const filteredProducts = data.products.filter(product => 
          product.title.toLowerCase().includes(queryLower) ||
          (product.tags && product.tags.some(tag => tag.toLowerCase().includes(queryLower)))
        );
        
        return { resources: { results: { products: filteredProducts } } };
      }
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('Request was cancelled');
    }
    
    throw new Error('All search methods failed');
  }

  displaySearchResults(data, query) {
    if (!this.searchResults) return;

    // Products already extracted and enriched
    const products = Array.isArray(data) ? data : this.extractProducts(data);
    
    if (!products || products.length === 0) {
      this.showNoResults(query);
      return;
    }

    const resultsHTML = this.buildResultsHTML(products, query);
    this.searchResults.innerHTML = resultsHTML;
    this.showResults();
  }

  async enrichWithVariants(products) {
    if (!products || products.length === 0) return products;
    const locale = window.Shopify?.locale;
    const localePrefix = locale && locale !== 'en' ? `/${locale}` : '';

    const enriched = await Promise.all(products.map(async (product) => {
      // Skip only if variants already have per-variant options array (only present after /products/handle.js fetch)
      if (product.variants && Array.isArray(product.variants) && product.variants.length > 0 && product.variants[0].options) {
        return product;
      }
      if (!product.handle) return product;
      try {
        // NOTE: /products/{handle}.js does NOT support locale prefixes — always use root path
        const res = await fetch(`/products/${product.handle}.js`, {
          signal: this.currentController?.signal
        });
        if (!res.ok) return product;
        const full = await res.json();
        return { ...product, variants: full.variants, options: full.options };
      } catch (e) {
        return product;
      }
    }));
    return enriched;
  }

  extractProducts(data) {
    if (data.resources?.results?.products) {
      return data.resources.results.products;
    }
    if (data.products) {
      return data.products;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  buildResultsHTML(products, query) {
    let html = `
      <div class="search-results-header">
        <h3 class="search-section-title">(${products.length}) results</h3>
      </div>
      <div class="search-products">
    `;

    products.forEach(product => {
      const imageUrl = ThemeUtils.getProductImageUrl(product);
      const price = this.getFormattedPrice(product);
      const productUrl = product.url || `/products/${product.handle}` || '#';

      const variantPillsHtml = this.buildVariantPillsHtml(product);
      const atcHtml = this.buildAtcButtonHtml(product);
      html += `
        <div class="search-product-item">
          <a href="${productUrl}" class="search-product-image-link">
            <div class="search-product-image">
              ${imageUrl 
                ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy" onerror="this.style.display='none'">` 
                : '<div class="search-product-no-image"></div>'}
            </div>
          </a>
          <div class="search-product-info">
            <a href="${productUrl}" class="search-product-title-link">
              <h4 class="card-product__title">${product.title}</h4>
            </a>
            ${variantPillsHtml}
            ${price ? `<span class="search-product-price body">${price}</span>` : ''}
            ${atcHtml}
          </div>
        </div>
      `;
    });

    html += '</div>';
    
    // Fixed footer with "See all" button
    const container = document.querySelector('.search-results-container');
    const seeAllLabel = (container && container.dataset.seeAllLabel) || 'See all search results';
    const localePrefix = window.Shopify?.locale && window.Shopify.locale !== 'en' ? `/${window.Shopify.locale}` : '';
    html += `
      <div class="search-results-footer">
        <a href="${localePrefix}/search?q=${encodeURIComponent(query)}" class="search-see-all-button">
          ${seeAllLabel}
        </a>
      </div>
    `;

    return html;
  }

  buildVariantPillsHtml(product) {
    const variants = product.variants || [];
    if (variants.length === 0) return '';
    const isSingleDefault = variants.length === 1 && variants[0].title === 'Default Title';
    if (isSingleDefault) return '';

    const firstAvailable = variants.find(v => v.available !== false) || variants[0];
    let html = '<div class="card-product__sizes" data-size-selector>';
    variants.forEach(v => {
      const avail = v.available !== false;
      const selected = v.id === firstAvailable.id ? ' is-selected' : '';
      const unavail = !avail ? ' is-unavailable' : '';
      const disabled = !avail ? ' disabled' : '';
      const label = this.getVariantLabel(v, product);
      html += `<button type="button" class="card-product__size-btn${selected}${unavail}" data-variant-id="${v.id}"${disabled}>${label}</button>`;
    });
    html += '</div>';
    return html;
  }

  buildAtcButtonHtml(product) {
    const variants = product.variants || [];
    const atcLabel = window.theme?.strings?.addToCart || '';
    const soldOutLabel = window.theme?.strings?.soldOut || '';

    if (variants.length === 0) {
      return `<div class="search-product-actions"><button class="card-product__quick-add-btn card-product__quick-add-btn--sold-out button-text" disabled><span class="card-product__quick-add-text">${soldOutLabel}</span></button></div>`;
    }

    const firstAvailable = variants.find(v => v.available !== false) || variants[0];
    const isAvailable = firstAvailable.available !== false;
    const btnClass = isAvailable
      ? 'card-product__quick-add-btn card-product__quick-add-btn--secondary button-text search-atc-btn'
      : 'card-product__quick-add-btn card-product__quick-add-btn--sold-out button-text search-atc-btn';
    return `<div class="search-product-actions"><button class="${btnClass}" data-variant-id="${firstAvailable.id}"${!isAvailable ? ' disabled' : ''}><span class="card-product__quick-add-text">${isAvailable ? atcLabel : soldOutLabel}</span></button></div>`;
  }

  getVariantLabel(variant, product) {
    // Prefer the size/volume option value if present; else fall back to full title
    const options = product.options || [];
    const sizeIdx = options.findIndex(o => {
      const name = (typeof o === 'string' ? o : o.name || '').toLowerCase();
      return /size|talla|beden|boyut|volume|hacim/.test(name);
    });
    if (sizeIdx >= 0 && variant.options && variant.options[sizeIdx]) {
      return variant.options[sizeIdx];
    }
    return variant.title;
  }

  getFormattedPrice(product) {
    const price = ThemeUtils.getProductPrice(product);
    return price ? ThemeUtils.formatMoney(price) : '';
  }

  getProductDescription(product) {
    const descriptionFields = ['body', 'description', 'body_html', 'excerpt', 'summary', 'content'];
    
    for (const field of descriptionFields) {
      if (product[field]) {
        const text = ThemeUtils.stripHtml(product[field]);
        return ThemeUtils.truncate(text, 100);
      }
    }
    
    return '';
  }

  // UI State Methods
  showSuggestions() {
    this.ensureElements();
    if (this.searchSuggestions) this.searchSuggestions.style.display = '';
    if (this.searchResults) this.searchResults.style.display = 'none';
    if (this.searchLoading) this.searchLoading.style.display = 'none';
  }

  showResults() {
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'none';
    if (this.searchResults) this.searchResults.style.display = 'block';
    if (this.searchLoading) this.searchLoading.style.display = 'none';
  }

  showLoading() {
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'none';
    if (this.searchResults) this.searchResults.style.display = 'none';
    if (this.searchLoading) this.searchLoading.style.display = 'flex';
  }

  showNoResults(query) {
    if (!this.searchResults) return;

    this.searchResults.innerHTML = `
      <div class="search-no-results">
        <h3 class="search-section-title">No results found for "${query}"</h3>
        <p>Try other search terms</p>
      </div>
    `;
    
    this.showResults();
  }

  showError() {
    if (!this.searchResults) return;

    this.searchResults.innerHTML = `
      <div class="search-error">
        <h3 class="search-section-title">Search error</h3>
        <p>An error occurred. Please try again.</p>
      </div>
    `;
    
    this.showResults();
  }

  clearSearch() {
    this.ensureElements();
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.toggleRemoveButton(false);
    this.showSuggestions();
  }

  toggleRemoveButton(show) {
    if (this.searchRemoveBtn) {
      this.searchRemoveBtn.style.display = show ? 'block' : 'none';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new DrawerSearch();
});

// Inject styles (kept for backward compatibility)
const searchStyles = `
  .search-results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
  }

  .search-products {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    padding-bottom: 80px;
  }

  @media (min-width: 1400px) {
    .search-products {
      grid-template-columns: 1fr 1fr;
    }
  }

  .search-product-item {
    overflow: hidden;
    transition: background-color 0.2s ease;
  }

  .search-product-item:hover {
    background: transparent;
    border-radius: 16px;
  }

  .search-product-link {
    display: flex;
    gap: 12px;
    padding: 8px;
    text-decoration: none;
    color: var(--color-dark-1, #000);
    flex-direction: row;
  }

  @media (min-width: 1400px) {
    .search-product-link {
      flex-direction: column;
      gap: 8px;
    }
  }

  .search-product-image {
    width: 120px;
    aspect-ratio: 3/4;
    overflow: hidden;
    background: var(--color-background-hover, #f5f5f5);
    flex-shrink: 0;
    position: relative;
    border-radius: 12px;
  }

  @media (min-width: 1400px) {
    .search-product-image {
      width: 100%;
      flex-shrink: 1;
    }
  }

  .search-product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.5s ease;
  }

  .search-product-item:hover .search-product-image img {
    transform: scale(1.02);
  }

  .search-product-no-image {
    width: 100%;
    height: 100%;
    background: var(--color-border, #e0e0e0);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted, #666);
    font-size: 12px;
  }

  .search-product-no-image::after {
    content: "No image";
  }

  .search-product-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media (min-width: 1400px) {
    .search-product-info {
      justify-content: flex-start;
      gap: 4px;
      padding-top: 8px;
    }
  }

  .search-product-title {
    margin: 0 0 4px 0;
  }

  .search-product-description {
    margin: 0 0 4px 0;
    color: var(--color-dark-1, #666);
    font-size: 14px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-product-price {
    font-size: 14px;
    color: var(--color-dark-1, #666);
  }

  .search-no-results,
  .search-error {
    text-align: center;
    padding: 40px 20px;
  }

  .search-no-results p,
  .search-error p {
    color: var(--color-dark-1, #666);
    margin-bottom: 20px;
  }

  .search-results-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    background: var(--color-brand-5-80);
    z-index: 1000;
  }

  .search-see-all-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 48px;
    padding: 0 16px;
    background: var(--color-brand-5-80);
    color: white;
    text-align: center;
    text-decoration: none;
    font-size: var(--font-button-size);
    line-height: var(--font-button-line);
    font-weight: var(--font-button-weight);
    letter-spacing: var(--font-button-letter);
    font-family: var(--font-button-family);
    text-transform: var(--font-button-case);
    border: 1px solid var(--color-brand-5-80);
    transition: all 0.2s ease;
  }

  .search-see-all-button:hover {
    background: var(--color-brand-5-100);
    border-color: var(--color-brand-5-100);
  }
`;

const styleSheet = document.createElement('style');
styleSheet.textContent = searchStyles;
document.head.appendChild(styleSheet);
